// Organiser-only review queue for public sign-ups. Registrations are submitted
// through the public QR link (see `RegistrationDialog`) and are not part of the
// tournament snapshot, so this view fetches them on demand from the
// authenticated `/registrations` endpoint. Approving a pending sign-up adds a
// player to the draft roster (handled server-side); rejecting it removes that
// player if it had been approved.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import type { Registration, RegistrationStatus } from '../domain';
import { fetchRegistrations } from '../data/apiClient';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';

// Chip labels are translated per-render via `admin.registrations.status.*`.
const STATUS_CHIP_COLOR: Record<RegistrationStatus, 'default' | 'success' | 'error'> = {
  // UNVERIFIED sign-ups have not confirmed their OTP yet and are filtered out
  // server-side, so they never reach this queue; mapped only for completeness.
  UNVERIFIED: 'default',
  PENDING: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
};

export function RegistrationsView() {
  const { t, localeCode } = useTranslation();
  const { tournament, approveRegistration, rejectRegistration, registrationsVersion } =
    useTournament();
  const id = tournament?.id ?? null;
  // Players can only be added/removed while the tournament is a draft, so
  // approvals/rejections are locked once it has started.
  const isDraft = tournament?.status === 'DRAFT';

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Id of the registration whose approve/reject call is in flight.
  const [busyId, setBusyId] = useState<string | null>(null);

  // The data loaders below must not re-run when the language changes (their
  // effects would refetch and flash the view), so they read the translator
  // through a ref that always holds the latest `t` without changing identity.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setRegistrations(await fetchRegistrations(id));
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.registrations.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Reload on mount/selection change, and whenever the server signals the
  // sign-up queue changed (a live event, possibly from another tab).
  useEffect(() => {
    void load();
  }, [load, registrationsVersion]);

  const act = useCallback(
    async (registrationId: string, action: 'approve' | 'reject') => {
      if (busyId) return;
      setBusyId(registrationId);
      setError(null);
      try {
        if (action === 'approve') await approveRegistration(registrationId);
        else await rejectRegistration(registrationId);
        await load();
      } catch (e: unknown) {
        setError(
          e instanceof Error
            ? e.message
            : t(`admin.registrations.actionError.${action}`),
        );
      } finally {
        setBusyId(null);
      }
    },
    [busyId, approveRegistration, rejectRegistration, load, t],
  );

  if (!tournament) return null;

  const pending = registrations.filter((r) => r.status === 'PENDING').length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.registrations.title')}
        </Typography>
        <Chip
          variant="outlined"
          label={t('admin.registrations.signUps', { count: registrations.length })}
        />
        {pending > 0 && (
          <Chip
            color="warning"
            label={t('admin.registrations.pendingCount', { count: pending })}
          />
        )}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          {t('actions.refresh')}
        </Button>
      </Stack>

      {!isDraft && registrations.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('admin.registrations.rosterLocked')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {/* Only the very first load shows the full-panel spinner; a live refetch
            (e.g. an SSE registrations echo) updates the list in place so it does
            not flash away and back. */}
        {loading && registrations.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : registrations.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
            <HowToRegIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {t('admin.registrations.empty')}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(150px, 2fr) minmax(100px, 1fr) minmax(130px, 1fr) minmax(120px, 1fr) 110px 180px',
              columnGap: 2,
              rowGap: 1.25,
              alignItems: 'center',
            }}
          >
            <Head>{t('registration.fullName')}</Head>
            <Head>{t('registration.dateOfBirth')}</Head>
            <Head>{t('admin.registrations.columns.mobile')}</Head>
            <Head>{t('admin.registrations.columns.registered')}</Head>
            <Head>{t('admin.registrations.columns.status')}</Head>
            <Head>{t('admin.common.actions')}</Head>
            {registrations.map((reg) => (
              <Box key={reg.id} sx={{ display: 'contents' }}>
                <Typography variant="body2" noWrap title={reg.fullName}>
                  {reg.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reg.dateOfBirth}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {reg.mobile}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatRegisteredAt(reg.createdAt, localeCode)}
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    color={STATUS_CHIP_COLOR[reg.status]}
                    variant={reg.status === 'PENDING' ? 'outlined' : 'filled'}
                    label={t(`admin.registrations.status.${reg.status}`)}
                  />
                </Box>
                <RowActions
                  status={reg.status}
                  isDraft={isDraft}
                  busy={busyId === reg.id}
                  anyBusy={busyId !== null}
                  onApprove={() => void act(reg.id, 'approve')}
                  onReject={() => void act(reg.id, 'reject')}
                />
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

interface RowActionsProps {
  status: RegistrationStatus;
  isDraft: boolean;
  busy: boolean;
  anyBusy: boolean;
  onApprove: () => void;
  onReject: () => void;
}

function RowActions({
  status,
  isDraft,
  busy,
  anyBusy,
  onApprove,
  onReject,
}: RowActionsProps) {
  const { t } = useTranslation();
  // Once the tournament has started the roster is locked; no actions are offered.
  if (!isDraft) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1}>
      {status !== 'APPROVED' && (
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
          disabled={anyBusy}
          onClick={onApprove}
        >
          {t('admin.registrations.approve')}
        </Button>
      )}
      {status !== 'REJECTED' && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<CloseIcon />}
          disabled={anyBusy}
          onClick={onReject}
        >
          {t('admin.registrations.reject')}
        </Button>
      )}
    </Stack>
  );
}

/** Short absolute date-time in the organiser's local timezone. */
function formatRegisteredAt(at: number, localeCode: string): string {
  return new Date(at).toLocaleString(localeCode, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Head({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
      {children}
    </Typography>
  );
}
